/**
 * EditModeProvider — owns the per-view edit stance, keyed by the active tab id.
 *
 * Only one view is shown in the centre pane at a time, but each tab remembers
 * its own stance while open: the flag lives in a `Map<tabId, boolean>` and the
 * active tab id (from the store) selects the current entry. Switching tabs
 * therefore restores that view's stance; closing the session drops the map
 * (ephemeral by design — see {@link EditModeContext}).
 *
 * A tab with no entry defaults to viewing (`false`). With no active tab the
 * stance is a no-op false — nothing to edit.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { EditModeContext, type EditModeApi } from "./EditModeContext.js";
import { useWorkspace } from "./WorkspaceContext.js";
import { useActiveTabId } from "./WorkspaceStore.js";

export function EditModeProvider({ children }: { readonly children: ReactNode }) {
  const store = useWorkspace();
  const activeTabId = useActiveTabId(store);
  const [byTab, setByTab] = useState<ReadonlyMap<string, boolean>>(() => new Map());

  // Prune stances for tabs that no longer exist — tab ids are deterministic (a
  // file tab's id IS its path), so without this a REOPENED tab would resurrect
  // the closed tab's edit stance instead of starting in viewing. Mirrors the
  // store's own per-tab view-record pruning on close. A DIRECT store
  // subscription (not an effect over the committed tab list): the store
  // notifies synchronously per mutation, so a close+reopen inside ONE handler
  // still surfaces the intermediate closed state here — the effect-over-props
  // version only ever saw the final list and let the stale stance survive.
  useEffect(() => {
    const prune = (): void => {
      // getLiveTabIds, not getState().tabs: the single-tab (mobile) collapse
      // hides every background tab transiently — pruning against the
      // collapsed list dropped stashed tabs' stances on a breakpoint
      // crossing (the store's own pruneViews keeps the stash alive too).
      const live = new Set(store.getLiveTabIds());
      setByTab((prev) => {
        let changed = false;
        const next = new Map<string, boolean>();
        for (const [id, on] of prev) {
          if (live.has(id)) next.set(id, on);
          else changed = true;
        }
        return changed ? next : prev;
      });
    };
    prune();
    const offSubscribe = store.subscribe(prune);
    // A workspace REPLACEMENT (replaceFiles / hydrate → "reload") resets every
    // per-tab view record in the store, but deterministic tab ids (board:…, a
    // same-path file) survive the swap — liveness pruning alone would carry
    // the old workspace's edit stance into the new one. Drop the whole map.
    const offReload = store.on((ev) => {
      if (ev.type === "reload" && ev.workspaceReplaced) {
        setByTab((prev) => (prev.size ? new Map() : prev));
      }
      // A rename re-keys the SAME logical tab (and its clones) — migrate the
      // stance via the EXACT id map the store computed (never an id-shape
      // guess: '#' is legal in file names) instead of letting the liveness
      // prune discard it (the open view silently snapped from edit back to
      // viewing otherwise).
      if (ev.type === "fileRenamed" && ev.renamedTabIds.length > 0) {
        const map = new Map(ev.renamedTabIds);
        setByTab((prev) => {
          let changed = false;
          const next = new Map<string, boolean>();
          for (const [id, on] of prev) {
            const dest = map.get(id);
            if (dest !== undefined) {
              next.set(dest, on);
              changed = true;
            } else {
              next.set(id, on);
            }
          }
          return changed ? next : prev;
        });
      }
    });
    return () => {
      offSubscribe();
      offReload();
    };
  }, [store]);

  const editMode = activeTabId ? (byTab.get(activeTabId) ?? false) : false;

  const setEditMode = useCallback(
    (on: boolean) => {
      if (!activeTabId) return;
      setByTab((prev) => {
        if ((prev.get(activeTabId) ?? false) === on) return prev;
        const next = new Map(prev);
        next.set(activeTabId, on);
        return next;
      });
    },
    [activeTabId],
  );

  const toggle = useCallback(() => setEditMode(!editMode), [setEditMode, editMode]);

  const value = useMemo<EditModeApi>(
    () => ({ editMode, setEditMode, toggle }),
    [editMode, setEditMode, toggle],
  );

  return <EditModeContext.Provider value={value}>{children}</EditModeContext.Provider>;
}
